import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface LoginFormData {
  email: string;
  password: string;
}

const AdminLogin: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const { login, user, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginFormData>();

  const from = (location.state as any)?.from?.pathname || '/admin/dashboard';

  useEffect(() => {
    if (user && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [user, isLoading, navigate, from]);

  const onSubmit = async (data: LoginFormData) => {
    const success = await login(data.email, data.password);
    if (success) {
      navigate(from, { replace: true });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (user) {
    return null; // Will redirect via useEffect
  }

  return (
    <>
      <Helmet>
        <title>Admin Login - Bali Getaway</title>
        <meta name="description" content="Admin login portal for Bali Getaway management system" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen flex">
        {/* Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          {/* Background Image */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: `url('https://images.unsplash.com/photo-1537996194471-e657df975ab4?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80')`
            }}
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-purple-900/70 to-slate-900/90" />

          {/* Decorative Glowing Orbs */}
          <div className="absolute top-1/4 -left-20 w-80 h-80 bg-orange-500/40 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-10 w-60 h-60 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />

          {/* Left Side Content */}
          <div className="relative z-10 flex flex-col justify-between h-full p-12">
            {/* Top - Logo & Branding */}
            <div>
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-xl blur-lg opacity-60" />
                  <div className="relative w-14 h-14 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-xl shadow-2xl flex items-center justify-center">
                    <img src="/logo.png" alt="Bali Getaway" className="h-9 w-9" />
                  </div>
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">Bali Getaway</h1>
                  <span className="text-sm font-medium bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent tracking-widest uppercase">Tropical Paradise</span>
                </div>
              </div>
            </div>

            {/* Center - Main Message */}
            <div className="max-w-md">
              <h2 className="text-5xl font-bold text-white leading-tight mb-6">
                Welcome to your<br />
                <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-yellow-400 bg-clip-text text-transparent">Admin Portal</span>
              </h2>
              <p className="text-xl text-white/70 leading-relaxed">
                Manage your travel packages, bookings, and create unforgettable experiences for your customers.
              </p>

              {/* Stats or Features */}
              <div className="flex items-center gap-8 mt-10">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">500+</div>
                  <div className="text-sm text-white/50">Bookings</div>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">50+</div>
                  <div className="text-sm text-white/50">Packages</div>
                </div>
                <div className="w-px h-12 bg-white/20"></div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">4.9★</div>
                  <div className="text-sm text-white/50">Rating</div>
                </div>
              </div>
            </div>

            {/* Bottom - Copyright */}
            <div>
              <p className="text-white/30 text-sm">© 2024 Bali Getaway. All rights reserved.</p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex-1 flex items-center justify-center p-4 sm:p-8 lg:p-12 relative overflow-hidden">
          {/* Dark Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />

          {/* Decorative Elements */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl" />

          {/* Form Container */}
          <div className="relative z-10 w-full max-w-md">
            {/* Mobile Logo - Only visible on mobile */}
            <div className="lg:hidden flex flex-col items-center mb-8">
              <div className="relative mb-4">
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-2xl blur-lg opacity-50" />
                <div className="relative w-16 h-16 bg-gradient-to-br from-orange-500 to-yellow-600 rounded-2xl shadow-2xl flex items-center justify-center">
                  <img src="/logo.png" alt="Bali Getaway" className="h-10 w-10" />
                </div>
              </div>
              <h1 className="text-2xl font-bold text-white">Bali Getaway</h1>
              <span className="text-xs font-medium bg-gradient-to-r from-orange-400 to-yellow-400 bg-clip-text text-transparent tracking-widest uppercase">Tropical Paradise</span>
            </div>

            {/* Form Card */}
            <div className="relative">
              {/* Gradient Border Effect */}
              <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 rounded-3xl blur opacity-20" />

              <div className="relative bg-slate-800/50 backdrop-blur-xl rounded-3xl shadow-2xl p-8 border border-white/10">
                {/* Form Header */}
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Sign In</h2>
                  <p className="text-white/50 text-sm">Access your admin dashboard</p>
                </div>

                {/* Login Form */}
                <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-white/70 mb-2">
                      Email Address
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      {...register('email', {
                        required: 'Email is required',
                        pattern: {
                          value: /^\S+@\S+$/i,
                          message: 'Please enter a valid email address'
                        }
                      })}
                      className={`w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:bg-white/10 outline-none transition-all ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                      placeholder="Enter your email"
                    />
                    {errors.email && (
                      <p className="mt-2 text-sm text-red-400">{errors.email.message}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-white/70 mb-2">
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        {...register('password', {
                          required: 'Password is required',
                          minLength: {
                            value: 6,
                            message: 'Password must be at least 6 characters'
                          }
                        })}
                        className={`w-full px-4 py-3.5 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/30 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 focus:bg-white/10 outline-none transition-all pr-12 ${errors.password ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20' : ''}`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-4 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5 text-white/40 hover:text-white/70 transition-colors" />
                        ) : (
                          <EyeIcon className="h-5 w-5 text-white/40 hover:text-white/70 transition-colors" />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="mt-2 text-sm text-red-400">{errors.password.message}</p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        className="h-4 w-4 bg-white/5 border-white/20 rounded text-orange-500 focus:ring-orange-500 focus:ring-offset-0 focus:ring-offset-transparent"
                      />
                      <label htmlFor="remember-me" className="ml-2 block text-sm text-white/60">
                        Remember me
                      </label>
                    </div>
                    <a href="#" className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors">
                      Forgot password?
                    </a>
                  </div>

                  {/* Premium Button with Shimmer */}
                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="relative w-full py-4 px-4 text-base font-bold text-white rounded-xl overflow-hidden transition-all duration-300 transform hover:scale-[1.02] disabled:opacity-70 disabled:cursor-not-allowed group"
                    >
                      {/* Button gradient background */}
                      <div className="absolute inset-0 bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500" />
                      {/* Shimmer effect */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                      {/* Button shadow */}
                      <div className="absolute inset-0 rounded-xl shadow-lg shadow-orange-500/30" />

                      <span className="relative">
                        {isLoading ? (
                          <span className="flex items-center justify-center">
                            <div className="loading-spinner w-5 h-5 mr-2"></div>
                            Signing in...
                          </span>
                        ) : (
                          'Sign In'
                        )}
                      </span>
                    </button>
                  </div>
                </form>

                {/* Footer */}
                <div className="mt-8 pt-6 border-t border-white/10 text-center">
                  <p className="text-xs text-white/40 flex items-center justify-center gap-2">
                    <span className="inline-block w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                    Secure admin area • All actions are logged
                  </p>
                </div>
              </div>
            </div>

            {/* Mobile Copyright */}
            <p className="lg:hidden text-center text-white/30 text-xs mt-6">
              © 2024 Bali Getaway. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminLogin;
